import { aUEC, parseAUEC } from '@itc-mod/bank/shared';
import { MILLI, fmtCount, fmtSCU, parseCount, parseSCU } from '@itc-mod/supply/shared';
import type { Line, LineUnit } from './schemas.ts';

// Amounts as Sierra types and shows them (plan 2C R11, sierra-panel.html lines
// 2231–2240 and 2422). aUEC parsing is the bank's `parseAUEC`, so `250k` is 250,000
// (port: the panel's donation sheet read it as 250).

export { aUEC, parseAUEC };

/** Parses a line's quantity as typed: thousandths of an SCU for SCU lines, a count otherwise. Null if unreadable. */
export function parseQuantity(unit: LineUnit, text: string): number | null {
  return unit === 'scu' ? parseSCU(text) : parseCount(text);
}

/** A line's quantity as shown: `2.5 SCU`, `3`, `4 kits`. */
export function quantityText(unit: LineUnit, quantity: number): string {
  if (unit === 'scu') return `${fmtSCU(quantity)} SCU`;
  if (unit === 'kits') return `${fmtCount(quantity)} kit${quantity === 1 ? '' : 's'}`;
  return fmtCount(quantity);
}

/** `donLineText`: `3× 8 SCU RMC`, `2.5 SCU Iron (refined Q500)`, `4 kits MedGel`, `12 units Nova Rifle`. */
export function lineText(line: Pick<Line, 'name' | 'category' | 'unit' | 'crateSize' | 'quantity' | 'quality' | 'refined'>): string {
  let text: string;
  if (line.unit === 'crates') text = `${fmtCount(line.quantity)}× ${String(line.crateSize ?? 0)} SCU ${line.name}`;
  else if (line.unit === 'scu') text = `${fmtSCU(line.quantity)} SCU ${line.name}`;
  else if (line.unit === 'kits') text = `${fmtCount(line.quantity)} kit${line.quantity === 1 ? '' : 's'} ${line.name}`;
  else text = `${fmtCount(line.quantity)} units ${line.name}`;
  if (line.category === 'Materials') text += ` (${line.refined ? 'refined' : 'unrefined'}${line.quality === null ? '' : ` Q${String(line.quality)}`})`;
  return text;
}

/** A donation as text: `5,000,000 aUEC`, or its lines joined. */
export function giftText(gift: { kind: 'auec' | 'items'; amount: number | null; lines: readonly Parameters<typeof lineText>[0][] }): string {
  return gift.kind === 'auec' ? aUEC(gift.amount ?? 0) : gift.lines.filter((line) => line.quantity > 0).map(lineText).join(', ');
}

/** `linesSCU`: the load, in thousandths of an SCU: SCU lines plus crates × size. Counted items and kits weigh nothing here. */
export function linesSCU(lines: readonly Pick<Line, 'unit' | 'crateSize' | 'quantity'>[]): number {
  return lines.reduce((sum, line) => sum + (line.unit === 'scu' ? line.quantity : line.unit === 'crates' ? line.quantity * (line.crateSize ?? 0) * MILLI : 0), 0);
}
